import React from 'react';
import { Shield, Lock, Globe, Cpu, Database, Network } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import Newsletter from '../components/Newsletter';

const GovernmentSolutions = () => {
  const solutions = [
    {
      icon: Shield,
      title: "AI-Driven Threat Detection",
      description: "Advanced machine learning systems that identify and neutralize cyber threats before they breach critical infrastructure"
    },
    {
      icon: Lock,
      title: "Cyber-Attack Prevention Frameworks",
      description: "Multi-layered defense systems protecting government networks, data centers, and essential services"
    },
    {
      icon: Database,
      title: "Secure Digital Identity & Data Governance",
      description: "Sovereign-grade identity management and data protection systems for national security"
    },
    {
      icon: Network,
      title: "Network Modernization",
      description: "Critical agency infrastructure upgrades with zero-trust architecture and encrypted communications"
    },
    {
      icon: Globe,
      title: "National Cyber-Readiness Programs",
      description: "Comprehensive training, simulation, and preparedness initiatives for government personnel"
    },
    {
      icon: Cpu,
      title: "Resilience Systems",
      description: "Failsafe infrastructure ensuring continuity of essential government operations during attacks"
    }
  ];

  const capabilities = [
    "Intelligence-grade security architecture",
    "24/7 threat monitoring and response",
    "Secure communications infrastructure",
    "Digital transformation with security-first design",
    "Compliance with international security standards",
    "Sovereign data protection frameworks",
    "Emergency response and recovery systems",
    "Cross-ministry integration capabilities"
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Cybersecurity Shield */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_consulting-synergy/artifacts/2o8fgjrx_C9E5A8C5-DFFA-4A79-99F6-D987A5FC4926.png"
            alt="Cybersecurity Shield"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/65"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center py-32">
          <h1 className="luxury-heading text-5xl md:text-7xl text-white mb-8 leading-tight">
            Cybersecurity, AI Defense &
            <span className="text-[#C9A55A]"> National Infrastructure Protection</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            Protecting the Middle East's digital and physical security through intelligent, proactive, sovereign-grade systems.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              asChild
              className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7 smooth-transition shadow-xl"
            >
              <Link to="/contact">
                Request Consultation
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-[#C9A55A] text-[#C9A55A] hover:bg-[#C9A55A] hover:text-black font-semibold text-lg px-12 py-7 smooth-transition"
            >
              <Link to="/services">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-20 bg-black"></div>

      {/* Solutions Grid */}
      <section className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Advanced Defense Solutions</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              Comprehensive cybersecurity and infrastructure protection for modern governments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Card key={index} className="luxury-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-[#C9A55A]/30 group cursor-pointer">
                <CardContent className="p-10">
                  <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-lg flex items-center justify-center mb-8 group-hover:bg-[#C9A55A]/20 smooth-transition border border-[#C9A55A]/20">
                    <solution.icon className="text-[#C9A55A]" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">{solution.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{solution.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Intelligence-Grade Security */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-8">
                Why Intelligence-Grade Security Matters
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  Modern governments face sophisticated threats from state actors, organized cybercrime, and advanced persistent threats. Standard commercial security is insufficient.
                </p>
                <p className="text-lg">
                  Our solutions are built on <span className="text-[#C9A55A] font-semibold">military-grade frameworks</span>, leveraging AI, machine learning, and real-time threat intelligence to protect critical national infrastructure.
                </p>
                <p className="text-lg italic text-gray-400">
                  We combine North American technology leadership with deep understanding of Middle Eastern security requirements.
                </p>
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-semibold text-white mb-6">Core Capabilities</h3>
                  <div className="space-y-4">
                    {capabilities.map((capability, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-[#C9A55A]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[#C9A55A] text-xs font-bold">✓</span>
                        </div>
                        <span className="text-gray-300 leading-relaxed">{capability}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* High-Security Equipment Procurement */}
      <section className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_consulting-synergy/artifacts/p09mqci2_612DDF8A-844F-46FE-9213-035A440E2FB9.png" 
                alt="Tactical Security Equipment"
                className="w-full h-auto"
              />
            </div>
            <div>
              <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-8">
                Procurement of High-Security Technologies & Equipment
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  Syndicate Consultants provides <span className="text-[#C9A55A] font-semibold">discreet, legal procurement</span> of specialized security equipment for government agencies and sovereign entities across the Middle East.
                </p>
                <p className="text-lg">
                  Our procurement channels include:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Tactical & Protective Equipment:</strong> Body armor, ballistic protection, tactical gear</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Surveillance Technologies:</strong> Advanced optical systems, monitoring equipment, reconnaissance tools</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Secure Communications:</strong> Encrypted hardware, secure transport cases, field equipment</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Defense-Compliant Procurement:</strong> All equipment sourced through legal, documented channels</span>
                  </li>
                </ul>
                <p className="text-gray-400 italic pt-4">
                  Every procurement is handled with absolute discretion, full compliance, and sovereign-grade professionalism.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ministry Applications */}
      <section className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Ministry Applications</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              Tailored solutions for government agencies and sovereign entities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Interior Ministry</h3>
                <p className="text-gray-400 mb-6">National security, border control, digital identity systems, and critical infrastructure protection</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Integrated border security systems</li>
                  <li>• National ID and biometric platforms</li>
                  <li>• Emergency response coordination</li>
                  <li>• Critical infrastructure monitoring</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Defense & Intelligence</h3>
                <p className="text-gray-400 mb-6">Secure communications, threat intelligence, and operational security systems</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Encrypted command networks</li>
                  <li>• Real-time threat intelligence</li>
                  <li>• Secure data storage and analysis</li>
                  <li>• Counter-intelligence frameworks</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Finance & Banking</h3>
                <p className="text-gray-400 mb-6">Financial system protection, fraud detection, and secure transaction infrastructure</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Banking system security</li>
                  <li>• AI fraud detection</li>
                  <li>• Secure payment networks</li>
                  <li>• Regulatory compliance systems</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Health & Public Services</h3>
                <p className="text-gray-400 mb-6">Healthcare data protection, patient privacy, and medical infrastructure security</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Patient data protection</li>
                  <li>• Hospital network security</li>
                  <li>• Medical device cybersecurity</li>
                  <li>• Health information exchanges</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-6">
            Secure Your Nation's Digital Future
          </h2>
          <p className="text-xl text-gray-400 mb-8 leading-relaxed">
            Contact us for a confidential security assessment and strategic consultation
          </p>
          <Button 
            asChild
            className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7"
          >
            <Link to="/contact">Request Consultation</Link>
          </Button>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default GovernmentSolutions;